import { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useLexicalEditable } from '@lexical/react/useLexicalEditable';
import {
  $getSelection,
  $isRangeSelection,
  $isElementNode,
  $isTextNode,
  $setSelection,
  COMMAND_PRIORITY_CRITICAL,
  SELECTION_CHANGE_COMMAND,
} from 'lexical';
import {
  $computeTableMapSkipCellCheck,
  $deleteTableColumnAtSelection,
  $deleteTableRowAtSelection,
  $getNodeTriplet,
  $getTableCellNodeFromLexicalNode,
  $getTableColumnIndexFromTableCellNode,
  $getTableNodeFromLexicalNodeOrThrow,
  $getTableRowIndexFromTableCellNode,
  $insertTableColumnAtSelection,
  $insertTableRowAtSelection,
  $isTableCellNode,
  $isTableSelection,
  $mergeCells,
  $moveTableColumn,
  $unmergeCell,
  getTableElement,
  getTableObserverFromTableElement,
  TableCellHeaderStates,
  TableCellNode,
} from '@lexical/table';
import { mergeRegister } from '@lexical/utils';
import { Check, ExpandLess, ExpandMore, KeyboardArrowDown, KeyboardArrowLeft, KeyboardArrowRight, VerticalAlignBottom, VerticalAlignCenter, VerticalAlignTop } from '@mui/icons-material';
import { Collapse, Divider, IconButton, ListItemIcon, ListItemText, MenuItem, MenuList, Paper } from '@mui/material';
import useModal from '../../hooks/useModal';
import ColorPicker from '../../ui/ColorPicker';
import Modal from '../../ui/Modal';

function computeSelectionCount(selection) {
  const selectionShape = selection.getShape();
  return {
    columns: selectionShape.toX - selectionShape.fromX + 1,
    rows: selectionShape.toY - selectionShape.fromY + 1,
  };
}

function $canUnmerge() {
  const selection = $getSelection();
  if (
    ($isRangeSelection(selection) && !selection.isCollapsed()) ||
    ($isTableSelection(selection) && !selection.anchor.is(selection.focus)) ||
    (!$isRangeSelection(selection) && !$isTableSelection(selection))
  ) {
    return false;
  }
  const [cell] = $getNodeTriplet(selection.anchor);
  return cell.__colSpan > 1 || cell.__rowSpan > 1;
}

function $selectLastDescendant(node) {
  const lastDescendant = node.getLastDescendant();
  if ($isTextNode(lastDescendant)) {
    lastDescendant.select();
  } else if ($isElementNode(lastDescendant)) {
    lastDescendant.selectEnd();
  } else if (lastDescendant !== null) {
    lastDescendant.selectNext();
  }
}

function currentCellBackgroundColor(editor) {
  return editor.getEditorState().read(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection) || $isTableSelection(selection)) {
      const [cell] = $getNodeTriplet(selection.anchor);
      if ($isTableCellNode(cell)) {
        return cell.getBackgroundColor();
      }
    }
    return null;
  });
}

function TableActionMenu({
  onClose,
  tableCellNode: _tableCellNode,
  setIsMenuOpen,
  contextRef,
  cellMerge,
  showColorPickerModal,
}) {
  const [editor] = useLexicalComposerContext();
  const dropDownRef = useRef(null);
  const [tableCellNode, updateTableCellNode] = useState(_tableCellNode);
  const [selectionCounts, updateSelectionCounts] = useState({
    columns: 1,
    rows: 1,
  });
  const [canMergeCells, setCanMergeCells] = useState(false);
  const [canUnmergeCell, setCanUnmergeCell] = useState(false);
  const [columnIndex, setColumnIndex] = useState(0);
  const [columnCount, setColumnCount] = useState(0);
  const [isRowStripingEnabled, setIsRowStripingEnabled] = useState(false);
  const [isFirstRowFrozen, setIsFirstRowFrozen] = useState(false);
  const [isFirstColumnFrozen, setIsFirstColumnFrozen] = useState(false);
  const [backgroundColor, setBackgroundColor] = useState(
    () => currentCellBackgroundColor(editor) || '',
  );
  const [expandVAlign, setExpandVAlign] = useState(false);

  const syncTableToggleStates = useCallback(() => {
    editor.getEditorState().read(() => {
      if (!tableCellNode?.isAttached()) {
        return;
      }
      const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);
      setIsRowStripingEnabled(tableNode.getRowStriping());
      setIsFirstRowFrozen(tableNode.getFrozenRows() > 0);
      setIsFirstColumnFrozen(tableNode.getFrozenColumns() > 0);
    });
  }, [editor, tableCellNode]);

  useEffect(() => {
    syncTableToggleStates();
  }, [syncTableToggleStates]);

  useEffect(() => {
    return editor.registerMutationListener(
      TableCellNode,
      (nodeMutations) => {
        const nodeUpdated =
          nodeMutations.get(tableCellNode.getKey()) === 'updated';
        if (nodeUpdated) {
          editor.getEditorState().read(() => {
            updateTableCellNode(tableCellNode.getLatest());
          });
          setBackgroundColor(currentCellBackgroundColor(editor) || '');
          syncTableToggleStates();
        }
      },
      { skipInitialization: true },
    );
  }, [editor, tableCellNode, syncTableToggleStates]);

  useEffect(() => {
    editor.getEditorState().read(() => {
      const selection = $getSelection();
      if ($isTableSelection(selection)) {
        const currentSelectionCounts = computeSelectionCount(selection);
        updateSelectionCounts(currentSelectionCounts);
        setCanMergeCells(
          currentSelectionCounts.columns > 1 || currentSelectionCounts.rows > 1,
        );
      }
      setCanUnmergeCell($canUnmerge());
      if (tableCellNode?.isAttached()) {
        const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);
        setIsRowStripingEnabled(tableNode.getRowStriping());
        setIsFirstRowFrozen(tableNode.getFrozenRows() > 0);
        setIsFirstColumnFrozen(tableNode.getFrozenColumns() > 0);
        setColumnIndex($getTableColumnIndexFromTableCellNode(tableCellNode));
        setColumnCount(tableNode.getColumnCount?.() ?? tableNode.getChildren().reduce((max, row) => Math.max(max, row.getChildrenSize()), 0));
      }
    });
  }, [editor, tableCellNode]);

  useEffect(() => {
    const menuButtonElement = contextRef.current;
    const dropDownElement = dropDownRef.current;
    const rootElement = editor.getRootElement();
    if (
      menuButtonElement != null &&
      dropDownElement != null &&
      rootElement != null
    ) {
      const rootEleRect = rootElement.getBoundingClientRect();
      const menuButtonRect = menuButtonElement.getBoundingClientRect();
      dropDownElement.style.opacity = '1';
      const dropDownElementRect = dropDownElement.getBoundingClientRect();
      const margin = 5;
      let leftPosition = menuButtonRect.right + margin;
      if (
        leftPosition + dropDownElementRect.width > window.innerWidth ||
        leftPosition + dropDownElementRect.width > rootEleRect.right
      ) {
        const position =
          menuButtonRect.left - dropDownElementRect.width - margin;
        leftPosition = (position < 0 ? margin : position) + window.pageXOffset;
      }
      dropDownElement.style.left = `${leftPosition + window.pageXOffset}px`;
      let topPosition = menuButtonRect.top;
      if (topPosition + dropDownElementRect.height > window.innerHeight) {
        const position = menuButtonRect.bottom - dropDownElementRect.height;
        topPosition = position < 0 ? margin : position;
      }
      dropDownElement.style.top = `${topPosition}px`;
    }
  }, [contextRef, dropDownRef, editor]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        dropDownRef.current != null &&
        contextRef.current != null &&
        event.target instanceof Node &&
        !dropDownRef.current.contains(event.target) &&
        !contextRef.current.contains(event.target)
      ) {
        setIsMenuOpen(false);
      }
    }
    window.addEventListener('click', handleClickOutside);
    return () => window.removeEventListener('click', handleClickOutside);
  }, [setIsMenuOpen, contextRef]);

  const clearTableSelection = useCallback(() => {
    editor.update(() => {
      if (tableCellNode.isAttached()) {
        const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);
        const tableElement = getTableElement(
          tableNode,
          editor.getElementByKey(tableNode.getKey()),
        );
        if (tableElement === null) {
          throw new Error('Expected to find tableElement in DOM');
        }
        const tableObserver = getTableObserverFromTableElement(tableElement);
        if (tableObserver !== null) {
          tableObserver.$clearHighlight();
        }
        tableNode.markDirty();
        updateTableCellNode(tableCellNode.getLatest());
      }
      $setSelection(null);
    });
  }, [editor, tableCellNode]);

  const mergeTableCellsAtSelection = () => {
    editor.update(() => {
      const selection = $getSelection();
      if (!$isTableSelection(selection)) return;
      const nodes = selection.getNodes();
      const tableCells = nodes.filter($isTableCellNode);
      const targetCell = $mergeCells(tableCells);
      if (targetCell) {
        $selectLastDescendant(targetCell);
        onClose();
      }
    });
  };

  const unmergeTableCellsAtSelection = () => {
    editor.update(() => {
      $unmergeCell();
    });
  };

  const insertTableRowAtSelection = useCallback(
    (shouldInsertAfter) => {
      editor.update(() => {
        for (let i = 0; i < selectionCounts.rows; i++) {
          $insertTableRowAtSelection(shouldInsertAfter);
        }
        onClose();
      });
    },
    [editor, onClose, selectionCounts.rows],
  );

  const insertTableColumnAtSelection = useCallback(
    (shouldInsertAfter) => {
      editor.update(() => {
        for (let i = 0; i < selectionCounts.columns; i++) {
          $insertTableColumnAtSelection(shouldInsertAfter);
        }
        onClose();
      });
    },
    [editor, onClose, selectionCounts.columns],
  );

  const deleteTableRowAtSelection = useCallback(() => {
    editor.update(() => {
      $deleteTableRowAtSelection();
      onClose();
    });
  }, [editor, onClose]);

  const deleteTableAtSelection = useCallback(() => {
    editor.update(() => {
      const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);
      tableNode.remove();
      clearTableSelection();
      onClose();
    });
  }, [editor, tableCellNode, clearTableSelection, onClose]);

  const deleteTableColumnAtSelection = useCallback(() => {
    editor.update(() => {
      $deleteTableColumnAtSelection();
      onClose();
    });
  }, [editor, onClose]);

  const toggleTableRowIsHeader = useCallback(() => {
    editor.update(() => {
      const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);
      const tableRowIndex = $getTableRowIndexFromTableCellNode(tableCellNode);
      const [gridMap] = $computeTableMapSkipCellCheck(tableNode, null, null);
      const rowCells = new Set();
      const newStyle =
        tableCellNode.getHeaderStyles() ^ TableCellHeaderStates.ROW;
      for (let col = 0; col < gridMap[tableRowIndex].length; col++) {
        const mapCell = gridMap[tableRowIndex][col];
        if (!mapCell?.cell) continue;
        if (!rowCells.has(mapCell.cell)) {
          rowCells.add(mapCell.cell);
          mapCell.cell.setHeaderStyles(newStyle, TableCellHeaderStates.ROW);
        }
      }
      clearTableSelection();
      onClose();
    });
  }, [editor, tableCellNode, clearTableSelection, onClose]);

  const toggleTableColumnIsHeader = useCallback(() => {
    editor.update(() => {
      const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);
      const tableColumnIndex =
        $getTableColumnIndexFromTableCellNode(tableCellNode);
      const [gridMap] = $computeTableMapSkipCellCheck(tableNode, null, null);
      const columnCells = new Set();
      const newStyle =
        tableCellNode.getHeaderStyles() ^ TableCellHeaderStates.COLUMN;
      for (let row = 0; row < gridMap.length; row++) {
        const mapCell = gridMap[row][tableColumnIndex];
        if (!mapCell?.cell) continue;
        if (!columnCells.has(mapCell.cell)) {
          columnCells.add(mapCell.cell);
          mapCell.cell.setHeaderStyles(newStyle, TableCellHeaderStates.COLUMN);
        }
      }
      clearTableSelection();
      onClose();
    });
  }, [editor, tableCellNode, clearTableSelection, onClose]);

  const toggleRowStriping = useCallback(() => {
    editor.update(() => {
      if (tableCellNode.isAttached()) {
        const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);
        if (tableNode) {
          tableNode.setRowStriping(!tableNode.getRowStriping());
        }
      }
      clearTableSelection();
      onClose();
    });
  }, [editor, tableCellNode, clearTableSelection, onClose]);

  const toggleFirstRowFreeze = useCallback(() => {
    editor.update(() => {
      if (tableCellNode.isAttached()) {
        const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);
        if (tableNode) {
          tableNode.setFrozenRows(tableNode.getFrozenRows() === 0 ? 1 : 0);
        }
      }
      clearTableSelection();
      onClose();
    });
  }, [editor, tableCellNode, clearTableSelection, onClose]);

  const moveColumnLeft = useCallback(() => {
    if (columnIndex <= 0) return;
    editor.update(() => {
      const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);
      $moveTableColumn(tableNode, columnIndex, columnIndex - 1);
      onClose();
    });
  }, [editor, tableCellNode, columnIndex, onClose]);

  const moveColumnRight = useCallback(() => {
    if (columnIndex >= columnCount - 1) return;
    editor.update(() => {
      const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);
      $moveTableColumn(tableNode, columnIndex, columnIndex + 1);
      onClose();
    });
  }, [editor, tableCellNode, columnIndex, columnCount, onClose]);

  const toggleFirstColumnFreeze = useCallback(() => {
    editor.update(() => {
      if (tableCellNode.isAttached()) {
        const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);
        if (tableNode) {
          tableNode.setFrozenColumns(
            tableNode.getFrozenColumns() === 0 ? 1 : 0,
          );
        }
      }
      clearTableSelection();
      onClose();
    });
  }, [editor, tableCellNode, clearTableSelection, onClose]);

  const handleCellBackgroundColor = useCallback(
    (value) => {
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection) || $isTableSelection(selection)) {
          const [cell] = $getNodeTriplet(selection.anchor);
          if ($isTableCellNode(cell)) {
            cell.setBackgroundColor(value);
          }
          if ($isTableSelection(selection)) {
            const nodes = selection.getNodes();
            for (let i = 0; i < nodes.length; i++) {
              const node = nodes[i];
              if ($isTableCellNode(node)) {
                node.setBackgroundColor(value);
              }
            }
          }
        }
      });
    },
    [editor],
  );

  const formatVerticalAlign = (value) => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection) || $isTableSelection(selection)) {
        const [cell] = $getNodeTriplet(selection.anchor);
        if ($isTableCellNode(cell)) {
          cell.setVerticalAlign(value);
        }
        if ($isTableSelection(selection)) {
          const nodes = selection.getNodes();
          for (let i = 0; i < nodes.length; i++) {
            const node = nodes[i];
            if ($isTableCellNode(node)) {
              node.setVerticalAlign(value);
            }
          }
        }
      }
    });
  };

  let mergeCellButton = null;
  if (cellMerge) {
    if (canMergeCells) {
      mergeCellButton = (
        <MenuItem onClick={() => mergeTableCellsAtSelection()} dense>
          <ListItemText>Merge cells</ListItemText>
        </MenuItem>
      );
    } else if (canUnmergeCell) {
      mergeCellButton = (
        <MenuItem onClick={() => unmergeTableCellsAtSelection()} dense>
          <ListItemText>Unmerge cells</ListItemText>
        </MenuItem>
      );
    }
  }

  return createPortal(
    <Paper
      ref={dropDownRef}
      elevation={4}
      onClick={(e) => e.stopPropagation()}
      sx={{
        position: 'fixed',
        zIndex: 9999,
        opacity: 0,
        minWidth: 230,
        borderRadius: 1.5,
        overflow: 'hidden',
      }}
    >
      <MenuList dense disablePadding sx={{ py: 0.5 }}>
        {mergeCellButton}
        <MenuItem onClick={moveColumnLeft} disabled={columnIndex <= 0} dense>
          <ListItemIcon><KeyboardArrowLeft fontSize="small" /></ListItemIcon>
          <ListItemText>Move Column Left</ListItemText>
        </MenuItem>
        <MenuItem onClick={moveColumnRight} disabled={columnIndex >= columnCount - 1} dense>
          <ListItemIcon><KeyboardArrowRight fontSize="small" /></ListItemIcon>
          <ListItemText>Move Column Right</ListItemText>
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem
          onClick={() =>
            showColorPickerModal('Cell background color', () => (
              <ColorPicker
                color={backgroundColor}
                onChange={handleCellBackgroundColor}
              />
            ))
          }
          dense
        >
          <ListItemText>Background color</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => toggleRowStriping()} dense>
          <ListItemText>Toggle Row Striping</ListItemText>
          {isRowStripingEnabled && <Check fontSize="small" sx={{ ml: 'auto' }} />}
        </MenuItem>
        <MenuItem onClick={() => setExpandVAlign((v) => !v)} dense>
          <ListItemText>Vertical Align</ListItemText>
          {expandVAlign ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
        </MenuItem>
        <Collapse in={expandVAlign} timeout="auto" unmountOnExit>
          <MenuList dense disablePadding sx={{ pl: 1.5 }}>
            <MenuItem onClick={() => { formatVerticalAlign('top'); setExpandVAlign(false); }} dense>
              <ListItemIcon><VerticalAlignTop fontSize="small" /></ListItemIcon>
              <ListItemText>Top Align</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => { formatVerticalAlign('middle'); setExpandVAlign(false); }} dense>
              <ListItemIcon><VerticalAlignCenter fontSize="small" /></ListItemIcon>
              <ListItemText>Middle Align</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => { formatVerticalAlign('bottom'); setExpandVAlign(false); }} dense>
              <ListItemIcon><VerticalAlignBottom fontSize="small" /></ListItemIcon>
              <ListItemText>Bottom Align</ListItemText>
            </MenuItem>
          </MenuList>
        </Collapse>
        <MenuItem onClick={() => toggleFirstRowFreeze()} dense>
          <ListItemText>Toggle First Row Freeze</ListItemText>
          {isFirstRowFrozen && <Check fontSize="small" sx={{ ml: 'auto' }} />}
        </MenuItem>
        <MenuItem onClick={() => toggleFirstColumnFreeze()} dense>
          <ListItemText>Toggle First Column Freeze</ListItemText>
          {isFirstColumnFrozen && <Check fontSize="small" sx={{ ml: 'auto' }} />}
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem onClick={() => insertTableRowAtSelection(false)} dense>
          <ListItemText>
            Insert{' '}
            {selectionCounts.rows === 1 ? 'row' : `${selectionCounts.rows} rows`}{' '}
            above
          </ListItemText>
        </MenuItem>
        <MenuItem onClick={() => insertTableRowAtSelection(true)} dense>
          <ListItemText>
            Insert{' '}
            {selectionCounts.rows === 1 ? 'row' : `${selectionCounts.rows} rows`}{' '}
            below
          </ListItemText>
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem onClick={() => insertTableColumnAtSelection(false)} dense>
          <ListItemText>
            Insert{' '}
            {selectionCounts.columns === 1 ? 'column' : `${selectionCounts.columns} columns`}{' '}
            left
          </ListItemText>
        </MenuItem>
        <MenuItem onClick={() => insertTableColumnAtSelection(true)} dense>
          <ListItemText>
            Insert{' '}
            {selectionCounts.columns === 1 ? 'column' : `${selectionCounts.columns} columns`}{' '}
            right
          </ListItemText>
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem onClick={() => deleteTableColumnAtSelection()} dense>
          <ListItemText primaryTypographyProps={{ color: 'error' }}>Delete column</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => deleteTableRowAtSelection()} dense>
          <ListItemText primaryTypographyProps={{ color: 'error' }}>Delete row</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => deleteTableAtSelection()} dense>
          <ListItemText primaryTypographyProps={{ color: 'error' }}>Delete table</ListItemText>
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem onClick={() => toggleTableRowIsHeader()} dense>
          <ListItemText>
            {(tableCellNode.__headerState & TableCellHeaderStates.ROW) ===
            TableCellHeaderStates.ROW
              ? 'Remove'
              : 'Add'}{' '}
            row header
          </ListItemText>
        </MenuItem>
        <MenuItem onClick={() => toggleTableColumnIsHeader()} dense>
          <ListItemText>
            {(tableCellNode.__headerState & TableCellHeaderStates.COLUMN) ===
            TableCellHeaderStates.COLUMN
              ? 'Remove'
              : 'Add'}{' '}
            column header
          </ListItemText>
        </MenuItem>
      </MenuList>
    </Paper>,
    document.body,
  );
}

function TableCellActionMenuContainer({ anchorElem, cellMerge }) {
  const [editor] = useLexicalComposerContext();
  const menuButtonRef = useRef(null);
  const menuRootRef = useRef(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [tableCellNode, setTableMenuCellNode] = useState(null);
  const [colorPickerModal, showColorPickerModal] = useModal();

  const $moveMenu = useCallback(() => {
    const menu = menuButtonRef.current;
    const selection = $getSelection();
    const activeElement = document.activeElement;

    function disable() {
      if (menu) {
        menu.classList.remove('table-cell-action-button-container--active');
        menu.classList.add('table-cell-action-button-container--inactive');
      }
      setTableMenuCellNode(null);
    }

    if (selection == null || menu == null) {
      return disable();
    }

    const rootElement = editor.getRootElement();
    let tableObserver = null;
    let tableCellParentNodeDOM = null;
    // positionNodeDOM is the cell used to anchor the button's position.
    // For multi-cell selections we resolve it to the top-right selected cell
    // so the button always appears outside the blue highlight area.
    let positionNodeDOM = null;

    if ($isRangeSelection(selection) && rootElement !== null) {
      const tableCellNodeFromSelection = $getTableCellNodeFromLexicalNode(
        selection.anchor.getNode(),
      );
      if (tableCellNodeFromSelection == null) {
        return disable();
      }
      tableCellParentNodeDOM = editor.getElementByKey(
        tableCellNodeFromSelection.getKey(),
      );
      if (
        tableCellParentNodeDOM == null ||
        !tableCellNodeFromSelection.isAttached()
      ) {
        return disable();
      }
      const tableNode = $getTableNodeFromLexicalNodeOrThrow(
        tableCellNodeFromSelection,
      );
      const tableElement = getTableElement(
        tableNode,
        editor.getElementByKey(tableNode.getKey()),
      );
      if (tableElement === null) {
        throw new Error('Expected to find tableElement in DOM');
      }
      tableObserver = getTableObserverFromTableElement(tableElement);
      setTableMenuCellNode(tableCellNodeFromSelection);
    } else if ($isTableSelection(selection)) {
      const anchorNode = $getTableCellNodeFromLexicalNode(
        selection.anchor.getNode(),
      );
      if (!$isTableCellNode(anchorNode)) {
        throw new Error('TableSelection anchorNode must be a TableCellNode');
      }
      const tableNode = $getTableNodeFromLexicalNodeOrThrow(anchorNode);
      const tableElement = getTableElement(
        tableNode,
        editor.getElementByKey(tableNode.getKey()),
      );
      if (tableElement === null) {
        throw new Error('Expected to find tableElement in DOM');
      }
      tableObserver = getTableObserverFromTableElement(tableElement);
      tableCellParentNodeDOM = editor.getElementByKey(anchorNode.getKey());
      if (tableCellParentNodeDOM === null) {
        return disable();
      }

      // Find the top-right cell of the selection to position the button
      // outside the highlighted area.
      const selectionShape = selection.getShape();
      const [gridMap] = $computeTableMapSkipCellCheck(tableNode, null, null);
      const topRightMapCell = gridMap[selectionShape.fromY]?.[selectionShape.toX];
      if (topRightMapCell?.cell) {
        const topRightDOM = editor.getElementByKey(topRightMapCell.cell.getKey());
        if (topRightDOM) positionNodeDOM = topRightDOM;
      }

      setTableMenuCellNode(anchorNode);
    } else if (!activeElement) {
      return disable();
    }

    if (tableObserver === null || tableCellParentNodeDOM === null) {
      return disable();
    }

    const enabled = !tableObserver || !tableObserver.isSelecting || $isTableSelection(selection);
    menu.classList.toggle(
      'table-cell-action-button-container--active',
      enabled,
    );
    menu.classList.toggle(
      'table-cell-action-button-container--inactive',
      !enabled,
    );

    if (enabled) {
      const rectSource = positionNodeDOM ?? tableCellParentNodeDOM;
      const tableCellRect = rectSource.getBoundingClientRect();
      const anchorRect = anchorElem.getBoundingClientRect();
      const top = tableCellRect.top - anchorRect.top;
      const left = tableCellRect.right - anchorRect.left;
      menu.style.transform = `translate(${left}px, ${top}px)`;
    }
  }, [editor, anchorElem]);

  useEffect(() => {
    let timeoutId;
    const callback = () => {
      timeoutId = undefined;
      editor.getEditorState().read($moveMenu);
    };
    const delayedCallback = () => {
      if (timeoutId === undefined) {
        timeoutId = setTimeout(callback, 0);
      }
      return false;
    };
    return mergeRegister(
      editor.registerUpdateListener(delayedCallback),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        delayedCallback,
        COMMAND_PRIORITY_CRITICAL,
      ),
      editor.registerRootListener((rootElement, prevRootElement) => {
        if (prevRootElement) {
          prevRootElement.removeEventListener('pointerup', delayedCallback);
          prevRootElement.removeEventListener('mouseup', delayedCallback);
          prevRootElement.removeEventListener('touchend', delayedCallback);
        }
        if (rootElement) {
          rootElement.addEventListener('pointerup', delayedCallback);
          rootElement.addEventListener('mouseup', delayedCallback);
          rootElement.addEventListener('touchend', delayedCallback);
          delayedCallback();
        }
      }),
      () => clearTimeout(timeoutId),
    );
  });

  const prevTableCellDOM = useRef(tableCellNode);

  useEffect(() => {
    if (prevTableCellDOM.current !== tableCellNode) {
      setIsMenuOpen(false);
    }
    prevTableCellDOM.current = tableCellNode;
  }, [prevTableCellDOM, tableCellNode]);

  return (
    <div className="table-cell-action-button-container" ref={menuButtonRef}>
      {tableCellNode != null && (
        <>
          <IconButton
            size="small"
            className={
              'table-cell-action-button' +
              (isMenuOpen ? ' table-cell-action-button--open' : '')
            }
            onClick={(e) => {
              e.stopPropagation();
              setIsMenuOpen(!isMenuOpen);
            }}
            aria-label="Open table action menu"
            aria-expanded={isMenuOpen}
            ref={menuRootRef}
            sx={{ p: 0.25 }}
          >
            <KeyboardArrowDown fontSize="small" />
          </IconButton>
          {colorPickerModal != null && (
            <Modal
              onClose={colorPickerModal.onClose}
              title={colorPickerModal.title}
              closeOnClickOutside={true}
            >
              {colorPickerModal.content}
            </Modal>
          )}
          {isMenuOpen && (
            <TableActionMenu
              contextRef={menuRootRef}
              setIsMenuOpen={setIsMenuOpen}
              onClose={() => setIsMenuOpen(false)}
              tableCellNode={tableCellNode}
              cellMerge={cellMerge}
              showColorPickerModal={showColorPickerModal}
            />
          )}
        </>
      )}
    </div>
  );
}

export default function TableActionMenuPlugin({
  anchorElem = document.body,
  cellMerge = true,
}) {
  const isEditable = useLexicalEditable();
  return createPortal(
    isEditable ? (
      <TableCellActionMenuContainer
        anchorElem={anchorElem}
        cellMerge={cellMerge}
      />
    ) : null,
    anchorElem,
  );
}
